import React, { useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useModalStyles } from "../../common/useModalStyles";

interface MessageMetaChipProps {
  msg: any;
  activeTab: string;
  userId: number;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  const { tokens: mTokens, isDark } = useModalStyles();
  return (
    <Box
      display="flex"
      gap={2}
      alignItems="flex-start"
      sx={{
        py: 0.6,
        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        sx={{
          minWidth: 52,
          textAlign: "right",
          fontSize: "0.72rem",
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: mTokens.textSecondary,
          flexShrink: 0,
          pt: "2px",
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          fontSize: "0.8rem",
          color: mTokens.textPrimary,
          lineHeight: 1.5,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const resolveNames = (msg: any, ...keys: string[]): string[] => {
  for (const key of keys) {
    const v = msg[key];
    if (!v) continue;
    if (Array.isArray(v) && v.length > 0) return v.map(String);
    if (typeof v === "string" && v.trim()) return [v.trim()];
  }
  return [];
};

const resolveIds = (msg: any, ...keys: string[]): number[] => {
  for (const key of keys) {
    const v = msg[key];
    if (!v) continue;
    if (Array.isArray(v) && v.length > 0) return v.map(Number);
    if (typeof v === "number") return [v];
  }
  return [];
};

const resolveEmails = (msg: any, ...keys: string[]): string[] => {
  for (const key of keys) {
    const v = msg[key];
    if (!v) continue;
    if (Array.isArray(v) && v.length > 0) return v.map(String);
    if (typeof v === "string" && v.trim()) return [v.trim()];
  }
  return [];
};


interface PersonPillProps {
  name: string;
  email?: string;
  color: string;
  bg: string;
  border: string;
}

const PersonPill: React.FC<PersonPillProps> = ({ name, email, color, bg, border }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      flexDirection: "column",
      px: 0.75,
      py: 0.25,
      borderRadius: "10px",
      fontSize: "0.73rem",
      bgcolor: bg,
      color,
      border: `1px solid ${border}`,
      fontWeight: 500,
      lineHeight: 1.4,
    }}
  >
    <span>{name}</span>
    {email && (
      <span style={{ opacity: 0.75, fontSize: "0.68rem", fontWeight: 400 }}>
        {email}
      </span>
    )}
  </Box>
);

const MessageMetaChip: React.FC<MessageMetaChipProps> = ({ msg, activeTab, userId }) => {
  const { tokens: mTokens, isDark } = useModalStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const toNames  = resolveNames(msg, "targetName", "targetNames", "toNames", "recipientNames");
  const toEmails = resolveEmails(msg, "targetEmail", "targetEmails", "toEmails", "recipientEmails");
  const toIds    = resolveIds(msg, "targetEntityId", "targetUserId", "targetEntityIds");

  const ccNames  = resolveNames(msg, "ccNames", "ccUserNames", "cc_names", "ccName", "cc");
  const ccEmails = resolveEmails(msg, "ccEmails", "ccUserEmails", "cc_emails", "ccEmail");

  const bccNames  = resolveNames(msg, "bccNames", "bccUserNames", "bcc_names", "bccName", "bcc");
  const bccEmails = resolveEmails(msg, "bccEmails", "bccUserEmails", "bcc_emails", "bccEmail");
  const bccIds    = resolveIds(msg, "bccIds");

  const isBccRecipient =
    activeTab !== "sent" &&
    bccIds.length > 0 &&
    bccIds.includes(userId);

  const bccSelfIdx   = isBccRecipient ? bccIds.indexOf(userId) : -1;
  const bccSelfName  = bccSelfIdx >= 0 ? (bccNames[bccSelfIdx]  ?? bccNames[0]  ?? "") : "";
  const bccSelfEmail = bccSelfIdx >= 0 ? (bccEmails[bccSelfIdx] ?? bccEmails[0] ?? "") : "";

  const hasCc = ccNames.length > 0;
  const hasBcc =
    activeTab === "sent"
      ? bccNames.length > 0
      : isBccRecipient && !!bccSelfName;

  const visibleBccNames  = activeTab === "sent" ? bccNames  : isBccRecipient ? [bccSelfName]  : [];
  const visibleBccEmails = activeTab === "sent" ? bccEmails : isBccRecipient ? [bccSelfEmail] : [];

  const buildSummary = () => {
    const parts: string[] = [];

    if (activeTab === "sent") {
      if (toNames.length) parts.push(`to ${toNames.join(", ")}`);
      if (hasCc)  parts.push(`cc ${ccNames.join(", ")}`);
      if (hasBcc) parts.push(`bcc ${bccNames.join(", ")}`);
    } else if (isBccRecipient) {
      if (toIds.includes(userId)) parts.push("to me");
      else if (toNames.length) parts.push(`to ${toNames.join(", ")}`);
      if (hasCc)  parts.push(`cc ${ccNames.join(", ")}`);
      if (hasBcc) parts.push(`bcc ${bccSelfName}`);
    } else {
      if (toIds.includes(userId)) parts.push("to me");
      else if (toNames.length) parts.push(`to ${toNames.join(", ")}`);
      if (hasCc) parts.push(`cc ${ccNames.join(", ")}`);
    }

    return parts.join(", ") || "details";
  };

  const summary   = buildSummary();
  const truncated = summary.length > 42 ? summary.slice(0, 40) + "…" : summary;

  const formattedDate = msg.formattedDate
    ? `${msg.formattedDate} ${msg.formattedTime ?? ""}`.trim()
    : "";

  const senderEmail: string =
    msg.senderEmail ?? msg.senderMail ?? msg.fromEmail ?? "";

  return (
    <Box sx={{ display: "inline-flex", position: "relative" }}>
      <Box
        ref={anchorRef}
        onClick={(e) => { e.stopPropagation(); setOpen((p) => !p); }}
        display="inline-flex"
        alignItems="center"
        gap={0.25}
        sx={{
          cursor: "pointer",
          px: 1,
          py: 0.3,
          borderRadius: "20px",
          border: `1px solid ${open
            ? isDark ? "rgba(20,184,166,0.4)" : "rgba(20,184,166,0.35)"
            : isDark ? "rgba(255,255,255,0.1)"  : "rgba(0,0,0,0.1)"}`,
          bgcolor: open
            ? isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.06)"
            : isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
          transition: "all 0.15s ease",
          "&:hover": {
            border: `1px solid ${isDark ? "rgba(20,184,166,0.35)" : "rgba(20,184,166,0.3)"}`,
            bgcolor: isDark ? "rgba(20,184,166,0.07)" : "rgba(20,184,166,0.05)",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 500,
            lineHeight: 1,
            color: mTokens.textSecondary,
            letterSpacing: "0.01em",
          }}
        >
          {truncated}
        </Typography>
        {open
          ? <KeyboardArrowUp sx={{ fontSize: 14, color: mTokens.textSecondary, ml: 0.25 }} />
          : <KeyboardArrowDown sx={{ fontSize: 14, color: mTokens.textSecondary, ml: 0.25 }} />
        }
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1400 }}
        modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box onClick={(e) => e.stopPropagation()}>
            <Paper
              elevation={0}
              sx={{
                minWidth: 320,
                maxWidth: 460,
                borderRadius: "12px",
                border: `1px solid ${mTokens.border}`,
                bgcolor: mTokens.bg,
                overflow: "hidden",
                boxShadow: isDark
                  ? "0 8px 32px rgba(0,0,0,0.5)"
                  : "0 8px 32px rgba(0,0,0,0.12)",
              }}
            >
              <Box
                px={2}
                py={1.25}
                sx={{
                  bgcolor: mTokens.headerFooterBg,
                  borderBottom: `1px solid ${mTokens.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: mTokens.textSecondary,
                  }}
                >
                  Message details
                </Typography>
              </Box>

              <Box px={2} py={1}>

                <Row
                  label="from"
                  value={
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          bgcolor: isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          color: "#14b8a6",
                          flexShrink: 0,
                        }}
                      >
                        {(msg.senderName || "?")[0].toUpperCase()}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, lineHeight: 1.3 }}>
                          {msg.senderName || "Unknown"}
                        </Typography>
                        {senderEmail && (
                          <Typography sx={{ fontSize: "0.72rem", color: mTokens.textSecondary, lineHeight: 1.2 }}>
                            {senderEmail}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  }
                />
                {toNames.length > 0 && (
                  <Row
                    label="to"
                    value={
                      <Box display="flex" flexDirection="column" gap={0.25}>
                        {toNames.map((name, i) => (
                          <Box key={i}>
                            <Typography sx={{ fontSize: "0.8rem", lineHeight: 1.3 }}>
                              {name}
                            </Typography>
                            {toEmails[i] && (
                              <Typography sx={{ fontSize: "0.72rem", color: mTokens.textSecondary, lineHeight: 1.2 }}>
                                {toEmails[i]}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    }
                  />
                )}

                {hasCc && (
                  <Row
                    label="cc"
                    value={
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {ccNames.map((name, i) => (
                          <PersonPill
                            key={i}
                            name={name}
                            email={ccEmails[i]}
                            color={isDark ? "#5eead4" : "#0f766e"}
                            bg={isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)"}
                            border={isDark ? "rgba(20,184,166,0.2)" : "rgba(20,184,166,0.2)"}
                          />
                        ))}
                      </Box>
                    }
                  />
                )}
                {hasBcc && (
                  <Row
                    label="bcc"
                    value={
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {visibleBccNames.map((name, i) => (
                          <PersonPill
                            key={i}
                            name={name}
                            email={visibleBccEmails[i]}
                            color={isDark ? "#99f6e4" : "#115e59"}
                            bg={isDark ? "rgba(20,184,166,0.1)" : "rgba(13,148,136,0.08)"}
                            border={isDark ? "rgba(20,184,166,0.22)" : "rgba(13,148,136,0.22)"}
                          />
                        ))}
                      </Box>
                    }
                  />
                )}
                {msg.notificationSub && (
                  <Row
                    label="subject"
                    value={<span style={{ fontWeight: 500 }}>{msg.notificationSub}</span>}
                  />
                )}

                {formattedDate && (
                  <Row
                    label="date"
                    value={
                      <span style={{ color: mTokens.textSecondary, fontSize: "0.76rem" }}>
                        {formattedDate}
                      </span>
                    }
                  />
                )}
              </Box>

              {msg.important === 1 && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  px={2}
                  py={0.75}
                  sx={{
                    bgcolor: isDark ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.06)",
                    borderTop: `1px solid ${isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.2)"}`,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#f59e0b", flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: isDark ? "#fbbf24" : "#d97706", letterSpacing: "0.02em" }}>
                    Important
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default MessageMetaChip;